import { useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';
import { debug, getCardRemovalAnimationDuration } from '@/shared/lib';

import { ModalID, modalModel } from '@/entities/modal';
import { notificationModel } from '@/entities/notification';
import { reportsModel } from '@/entities/reports';

type HideReportOptions = {
  /** Called when the hide request fails so the caller can roll back optimistic UI. */
  onError?: () => void;
};

/**
 * Resolves `true` as soon as the user confirms so the card can start its
 * removal animation; the request itself is sent after the animation. A failed
 * request shows an error notification and reports back through `onError`.
 */
export const useHideReport = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  return async (
    reportIDs: string[],
    options?: HideReportOptions,
  ): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.Confirm,
          data: {
            title: formatMessage({
              id: 'hideReportModal.title',
              defaultMessage: 'Archive report',
            }),
            okText: formatMessage({
              id: 'hideReportModal.apply',
              defaultMessage: 'Yes, archive',
            }),
            description: formatMessage({
              id: 'hideReportModal.message',
              defaultMessage:
                'The report will be hidden from the patient page. It stays in the system and can be restored by the clinic owner or administrator.',
            }),
            onConfirm: async () => {
              dispatch(modalModel.actions.closeModal(ModalID.Confirm));
              resolve(true);

              setTimeout(async () => {
                try {
                  await Promise.all(
                    reportIDs.map((reportID) =>
                      dispatch(
                        reportsModel.thunks.archiveReport(reportID),
                      ).unwrap(),
                    ),
                  );
                } catch (error) {
                  debug('Failed to hide report:', error);

                  dispatch(
                    notificationModel.actions.addNotification({
                      id: Date.now().toString(),
                      type: 'error',
                      message: formatMessage({
                        id: 'hideReport.error',
                        defaultMessage:
                          'Failed to hide the report. Please try again.',
                      }),
                    }),
                  );
                  options?.onError?.();
                }
              }, getCardRemovalAnimationDuration());
            },
            onCancel: () => {
              resolve(false);
            },
          },
        }),
      );
    });
};

export const useRestoreReport = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  return async (reportIDs: string[]): Promise<boolean> => {
    try {
      await Promise.all(
        reportIDs.map((reportID) =>
          dispatch(reportsModel.thunks.restoreReport(reportID)).unwrap(),
        ),
      );

      return true;
    } catch (error) {
      debug('Failed to restore report:', error);

      dispatch(
        notificationModel.actions.addNotification({
          id: Date.now().toString(),
          type: 'error',
          message: formatMessage({
            id: 'restoreReport.error',
            defaultMessage: 'Failed to restore the report. Please try again.',
          }),
        }),
      );

      return false;
    }
  };
};
